import React from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import { assignNurseInRoom } from '@/graph/patients';
import { gql, useMutation, useSubscription } from '@apollo/client';
import StaffSlot from '@/components/pages/kiosk/PACUv2/StaffSlot';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CircularProgress from '@material-ui/core/CircularProgress';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import { PrimarySpecialtyColors } from '@/types/StaffMember';

export interface AssignedNurse {
  id?: number | string;
  staffId?: number | string;
  name?: string;
  primarySpecialty?: string;
}

export interface ConfirmationDialogRawProps {
  id: string;
  keepMounted: boolean;
  patientId: number;
  roomId: number;
  open: boolean;
  onClose: (value?: string) => void;
  /** Staff specialty filter; defaults to PACU for backward compatibility */
  primarySpecialty?: 'PACU' | 'PRE_OP' | 'OR' | 'SEDATION';
  /** Currently assigned nurse for this patient/room, if any */
  assignedNurse?: AssignedNurse | null;
}

function SelectPatientDialog(props: ConfirmationDialogRawProps) {
  const { onClose, open, patientId, roomId, primarySpecialty = 'PACU', assignedNurse, ...other } = props;
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [pendingStaffId, setPendingStaffId] = React.useState<string | number | null | undefined>(undefined);
  const radioGroupRef = React.useRef<HTMLElement>(null);

  const { data, loading } = useSubscription(
    gql`
      subscription staffBySpecialtyIncludingSecondary($primarySpecialty: StaffPrimarySpecialty) {
        staffMembers: staffBySpecialtyIncludingSecondary(primarySpecialty: $primarySpecialty) {
          id
          staffId
          name
        }
      }
    `,
    {
      variables: {
        primarySpecialty,
      },
      skip: !open,
    }
  );

  const handleEntering = () => {
    if (radioGroupRef.current != null) {
      radioGroupRef.current.focus();
    }
  };

  const handleCancel = () => {
    if (submitting) return;
    onClose();
  };

  const [assignNurseMutation] = useMutation(assignNurseInRoom);

  const normalizeStaffId = (value?: string | number | null) => {
    if (value == null) return null;
    const asString = String(value);
    // Staff GraphQL `id` can be composite: "staffId.organizationId"
    return asString.includes('.') ? asString.split('.')[0] : asString;
  };

  const assignedStaffId = normalizeStaffId(assignedNurse?.staffId ?? assignedNurse?.id);

  const handleChange = async (staffId: string | number | null) => {
    if (submitting) return;
    if (staffId != null && assignedStaffId != null && normalizeStaffId(staffId) === assignedStaffId) {
      return;
    }

    setSubmitting(true);
    setPendingStaffId(staffId);
    try {
      await assignNurseMutation({
        variables: {
          id: patientId,
          staffId,
          roomId,
        },
      });

      onClose();
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
      setPendingStaffId(undefined);
    }
  };

  const filteredStaffMembers = React.useMemo(() => {
    if (!data?.staffMembers) return [];
    return data.staffMembers.filter(staff => staff.name.toLowerCase().includes(searchTerm.toLowerCase()));
  }, [data, searchTerm]);

  const columnCount = React.useMemo(() => {
    const count = filteredStaffMembers.length;
    if (count > 30) return 3;
    if (count > 15) return 2;
    return 1;
  }, [filteredStaffMembers.length]);

  React.useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSubmitting(false);
      setPendingStaffId(undefined);
    }
  }, [open]);

  const isUnassigning = submitting && pendingStaffId === null;

  return (
    <Dialog
      maxWidth="md"
      onEntering={handleEntering}
      aria-labelledby="confirmation-dialog-title"
      open={open}
      onClose={handleCancel}
      classes={{
        paper: classes.dialogPaper,
      }}
      {...other}
    >
      <DialogTitle id="confirmation-dialog-title">Assign nurse to patient</DialogTitle>
      <DialogContent dividers>
        <Box className={classes.assignedSection}>
          <Typography variant="subtitle1" color="textSecondary">
            Currently assigned
          </Typography>
          {assignedNurse?.name ? (
            <Box className={classes.assignedRow}>
              <Chip
                className={classes.assignedChip}
                icon={
                  <RadioButtonUncheckedIcon
                    style={{
                      color:
                        (assignedNurse?.primarySpecialty
                          ? PrimarySpecialtyColors[assignedNurse.primarySpecialty]
                          : undefined) ?? 'red',
                    }}
                  />
                }
                label={assignedNurse.name}
              />
              <Button color="secondary" variant="outlined" disabled={submitting} onClick={() => handleChange(null)}>
                {isUnassigning ? 'Unassigning…' : 'Unassign'}
              </Button>
            </Box>
          ) : (
            <Typography variant="body1">No nurse assigned</Typography>
          )}
        </Box>
        <Box className={classes.searchContainer}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search nurses..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            disabled={submitting}
          />
        </Box>
        {loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : filteredStaffMembers.length === 0 ? (
          <Box className={classes.emptyContainer}>
            <Typography variant="h6" color="textSecondary">
              {searchTerm.trim() ? 'No nurses found' : 'No nurses available'}
            </Typography>
            {searchTerm.trim() && (
              <Typography variant="body2" color="textSecondary">
                Try a different search term
              </Typography>
            )}
          </Box>
        ) : (
          <Box className={classes.staffList} style={{ gridTemplateColumns: `repeat(${columnCount}, 1fr)` }}>
            {filteredStaffMembers.map(staffMember => {
              const isSelected =
                assignedStaffId != null && normalizeStaffId(staffMember.staffId) === assignedStaffId;
              const isPending =
                submitting &&
                pendingStaffId != null &&
                normalizeStaffId(staffMember.staffId) === normalizeStaffId(pendingStaffId);
              return (
                <StaffSlot
                  key={staffMember.id}
                  name={staffMember.name}
                  selected={isSelected}
                  disabled={submitting}
                  loading={isPending}
                  onClick={() => handleChange(staffMember.staffId)}
                />
              );
            })}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          onClick={handleCancel}
          color="primary"
          size="large"
          disabled={submitting}
          className={classes.cancelButton}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    width: '80%',
    maxHeight: '80vh',
  },
  assignedSection: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  assignedRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  assignedChip: {
    height: 36,
    fontSize: '1rem',
  },
  cancelButton: {
    minHeight: 48,
    minWidth: 120,
  },
  searchContainer: {
    marginBottom: theme.spacing(2),
  },
  staffList: {
    display: 'grid',
    gap: theme.spacing(1),
    overflowY: 'auto',
    maxHeight: '60vh',
    paddingRight: theme.spacing(1),
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: '3px',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
    gap: theme.spacing(1),
  },
}));

export default SelectPatientDialog;
